import {AsyncPipe} from '@angular/common';
import {ChangeDetectionStrategy, ChangeDetectorRef, Component, effect, Injector, input, OnInit} from '@angular/core';
import {Subject} from 'rxjs';
import {audit, startWith, takeUntil, tap} from 'rxjs/operators';
import {BigintDisplayDecimalPipe} from '../../../../components/bigint-display-decimal-pipe/bigint-display-decimal.pipe';
import {
    ValueWithDelDisplayDecimalPipe
} from '../../../../components/bigint-display-decimal-pipe/value-with-del-display-decimal.pipe';
import {CustomScrollbarModule} from '../../../../components/custom-scrollbar';
import {destroy$} from '../../../../components/destroyable';
import {
    ComputedTrancheHeldPosition
} from '../../../../components/dex/dex-streaming/types/computed/computed-held-position';
import {ComputedTranche} from '../../../../components/dex/dex-streaming/types/computed/computed-tranche';
import {DisplayDecimalModule} from '../../../../components/display-decimal';
import {ElementRulerModule} from '../../../../components/element-ruler';
import {WIDGET_UPDATES_GATING$} from '../../../../components/layout/widget/widget-updates-gating';
import {TablesModule} from '../../../../components/tables';
import {
    getDefaultColumnsConfigByDef,
    normalizeColumnsConfig,
    TableColumnsSizingMode
} from '../../../../components/tables/table';
import {TooltipModule} from '../../../../components/tooltip';
import {L10nModule} from '../../../../components/translate';
import {TextHidingModule} from '../../../../directives/text-hiding';
import {SparklineModule} from '../../../../mobile/pages/m-quotes-page/m-quotes-mini-chart/sparkline';
import {DexTrancheSparklinePipe} from '../../dex-tranche-sparkline.pipe';
import {
    DEX_TRANCHE_COMPOS_TABLE_COLUMNS_DEF,
    DEX_TRANCHE_COMPOS_TABLE_COLUMNS_DICT,
    DEX_TRANCHE_COMPOS_TABLE_COLUMNS_SIZING,
    DexDisplayTrancheComposItem,
    DexTrancheComposTable,
    DexTrancheComposTableColumnConfig
} from './dex-tranche-compos-table';
import {DexTrancheTableTickPipe} from './dex-tranche-table-tick.pipe';

const EMPTY_ROW = {} as DexDisplayTrancheComposItem;

@Component({
    selector:        'dex-tranche-compos-table',
    templateUrl:     './dex-tranche-compos-table.component.html',
    styleUrl:        './dex-tranche-compos-table.component.scss',
    imports: [
        CustomScrollbarModule,
        ElementRulerModule,
        L10nModule,
        TablesModule,
        TextHidingModule,
        TooltipModule,
        DisplayDecimalModule,
        DexTrancheTableTickPipe,
        SparklineModule,
        ValueWithDelDisplayDecimalPipe,
        BigintDisplayDecimalPipe,
        AsyncPipe,
        DexTrancheSparklinePipe
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DexTrancheComposTableComponent implements OnInit {

    public readonly tranche = input.required<ComputedTranche>();

    public readonly table: DexTrancheComposTable = {
        columnsDict:        DEX_TRANCHE_COMPOS_TABLE_COLUMNS_DICT,
        columnsConfig:      normalizeColumnsConfig(
            DEX_TRANCHE_COMPOS_TABLE_COLUMNS_DEF,
            getDefaultColumnsConfigByDef(DEX_TRANCHE_COMPOS_TABLE_COLUMNS_DEF)
        ),
        columnsSizingSteps: DEX_TRANCHE_COMPOS_TABLE_COLUMNS_SIZING,
        columnsSizingMode:  TableColumnsSizingMode.AUTO,
        rows:               [],
        emptyRow:           EMPTY_ROW,
        columnsUpdate$:     new Subject<DexTrancheComposTableColumnConfig[]>()
    };

    private readonly triggerUpdateRows$ = new Subject<ComputedTrancheHeldPosition[]>();

    private readonly destroy$ = destroy$();

    constructor(
        private readonly injector: Injector,
        private readonly changeDetectorRef: ChangeDetectorRef
    ) {}

    public ngOnInit(): void {
        effect(() => {
            this.triggerUpdateRows$.next(this.tranche().$computedPositions());
        }, {injector: this.injector});

        this.initRowsUpdating();
    }

    public getTypedRow(row: DexDisplayTrancheComposItem): DexDisplayTrancheComposItem {
        return row;
    }

    private initRowsUpdating(): void {
        this.triggerUpdateRows$.pipe(
            startWith(this.tranche().$computedPositions()),
            audit(() => WIDGET_UPDATES_GATING$),
            tap(ps => this.updateRows(ps)),
            takeUntil(this.destroy$)
        ).subscribe();
    }

    private updateRows(positions: ComputedTrancheHeldPosition[]): void {
        this.table.rows = positions.map(pos => {
            const symbol = pos.deliverable?.symbol || `#${pos.raw.identifier.deliverableId}`;

            return {
                raw:     pos,
                percent: pos.percent,
                symbol,
                tick:    pos.isDepositAllowed
            } satisfies DexDisplayTrancheComposItem;
        });

        this.sortRows();
        this.changeDetectorRef.markForCheck();
    }

    private sortRows(): void {
        const trDelId = this.tranche().raw.unitDeliverableId;

        this.table.rows.sort((a, b) => {
            const aDelId = a.raw.raw.identifier.deliverableId;
            const bDelId = b.raw.raw.identifier.deliverableId;

            return ((aDelId === trDelId ? 0 : 1) - (bDelId === trDelId ? 0 : 1)) // Base pos first
                || ((b.percent || 0) - (a.percent || 0)); // Larger first
        });
    }

}
